import * as React from "react";
import { Link } from "react-router";

import {
  MAX_MATCH_PATHS,
  samplePathsForPatterns,
} from "@/shared/domain/ingestionRules";
import {
  useIngestionRules,
  useIngestionRulesMatchingAny,
} from "@/shared/domain/ingestionRules/hooks";
import { useAllTriggers } from "@/shared/domain/triggers/hooks";
import { Banner } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import styles from "./IngestionRuleNudge.module.css";
import {
  overlappingIngestionTriggers,
  rulesWithFilesMatching,
} from "./ruleOverlaps";

/**
 * Warns when files this rule matches are already ingested by another rule
 * with auto-ingest on, or by a trigger someone wrote, since each of them
 * ingests the file.
 */
export function RuleOverlapWarning({
  includePatterns,
  ruleId,
}: {
  includePatterns: readonly string[];
  /** The rule being edited, which never overlaps itself. */
  ruleId?: string;
}) {
  const samplePaths = React.useMemo(
    () => samplePathsForPatterns(includePatterns).slice(0, MAX_MATCH_PATHS),
    [includePatterns],
  );
  const matchingQuery = useIngestionRulesMatchingAny(samplePaths);
  const rulesQuery = useIngestionRules();
  const triggersQuery = useAllTriggers();

  // Checked both ways round, like triggers: the rules matching this rule's
  // example paths, then the rules whose example paths this rule matches.
  const rules = React.useMemo(() => {
    const byId = new Map(
      [
        ...(matchingQuery.data ?? []),
        ...rulesWithFilesMatching(rulesQuery.data ?? [], includePatterns),
      ].map((rule) => [rule.rule_id, rule]),
    );
    return [...byId.values()].filter(
      (rule) => rule.rule_id !== ruleId && rule.auto_ingest,
    );
  }, [matchingQuery.data, rulesQuery.data, includePatterns, ruleId]);
  const triggers = React.useMemo(
    () =>
      overlappingIngestionTriggers(triggersQuery.data ?? [], includePatterns),
    [triggersQuery.data, includePatterns],
  );

  const overlaps = [
    ...rules.map((rule) => ({
      key: rule.rule_id,
      kind: "rule",
      name: rule.name,
      to: Goto.createIngestionRuleRoute(rule.rule_id),
    })),
    ...triggers.map((trigger) => ({
      key: trigger.trigger_id,
      kind: "trigger",
      name: trigger.name,
      to: Goto.createTriggerByIdRoute(trigger.trigger_id),
    })),
  ];
  if (overlaps.length === 0) {
    return null;
  }
  return (
    <Banner
      variant="warning"
      className={styles.nudge}
      data-testid="rule-overlap-warning"
    >
      Some files this rule matches are also ingested by{" "}
      {overlaps.map((overlap, index) => (
        <React.Fragment key={overlap.key}>
          {index > 0 && (index === overlaps.length - 1 ? " and " : ", ")}
          the {overlap.kind} <Link to={overlap.to}>{overlap.name}</Link>
        </React.Fragment>
      ))}
      . Files matching both are ingested twice.
    </Banner>
  );
}
